import { useCallback } from 'preact/hooks'
import { useSignal } from '@preact/signals'
import { MarkupText, Text } from 'preact-i18n'
import { Icon, Menu, MenuItemProps } from 'semantic-ui-react'
import BarcodeMenuOptionOverlay from './BarcodeMenuOptionOverlay'
import CameraDropDown from './CameraDropDown'
import { MainMenuItemContainer } from '../../atoms'

import { useStore, useTracker } from '../../../store'
import { MenuSettingName } from '../../../store/SettingsStore'
import { SymbologyOptionExtension } from '../../../store/SymbologyOption'
import { ScannerMode } from '../../../store/InitialOptionsStore'
import { Show } from '../../directives'

const MainMenu = () => {
  const activeSubMenu = useSignal('')
  const store = useStore()
  const tracker = useTracker()
  const batchModeEnabled = store.initialOptionsStore.scannerMode.value === ScannerMode.Batch

  const toggleSubmenu = useCallback(
    (_e: MouseEvent, data: MenuItemProps) => {
      // Only barcodes has submenu
      if (!['barcodes'].includes(data.name ?? '')) return
      if (activeSubMenu.value === data.name) {
        activeSubMenu.value = ''
        return
      }
      activeSubMenu.value = data.name ?? ''
    },
    [activeSubMenu]
  )

  const handleOnClick = useCallback(
    (_e: MouseEvent, { name }: MenuItemProps) => {
      const map: Record<MenuSettingName, () => void> = {
        duplicateFilter: async () => {
          tracker.sendEvent('Setting', 'duplicate-filter')
          await store.settingsStore.setDuplicateFilter(!store.settingsStore.duplicateFilter.value).apply()
        },
        soundEffects: async () => {
          tracker.sendEvent('Setting', 'sound')
          const settingsStore = await store.settingsStore.setSoundEffects(!store.settingsStore.soundEffects.value)
          await settingsStore.apply()
        },
      }
      if (map[name as MenuSettingName]) map[name as MenuSettingName]()
    },
    [store, tracker]
  )

  return (
    <>
      <Menu
        id="options-menu"
        inverted
        widths={batchModeEnabled ? 2 : 4}
        fluid
        compact
        icon="labeled"
        pointing
        size="small"
        onDoubleClick={(e: MouseEvent) => e.stopPropagation()}
      >
        <Menu.Item id="barcodes" name="barcodes" onClick={toggleSubmenu} active={activeSubMenu.value === 'barcodes'}>
          <MainMenuItemContainer active>
            <Icon name="barcode" />
            <div>
              <Text id="options.barcodes" />
            </div>
            <Icon name="dropdown" />
          </MainMenuItemContainer>
        </Menu.Item>
        <CameraDropDown />
        <Show when={!batchModeEnabled}>
          <Menu.Item id="soundEffects" name="soundEffects" onClick={handleOnClick}>
            <MainMenuItemContainer active={store.settingsStore.soundEffects.value}>
              <Icon name="music" />
              <MarkupText id="options.soundEffects" />
            </MainMenuItemContainer>
          </Menu.Item>
          <Menu.Item id="duplicateFilter" name="duplicateFilter" onClick={handleOnClick}>
            <MainMenuItemContainer active={store.settingsStore.duplicateFilter.value}>
              <Icon name="history" />
              <MarkupText id="options.duplicateFilter" />
            </MainMenuItemContainer>
          </Menu.Item>
        </Show>
      </Menu>
      <BarcodeMenuOptionOverlay
        open={activeSubMenu.value === 'barcodes'}
        symbologiesOptions={store.settingsStore.symbologiesOptions}
        symbologiesOptionsAddon={store.settingsStore.symbologiesAddonsOptions}
        toggleSymbology={useCallback((symbologyOption) => {
          tracker.sendEvent('Setting', 'barcode-symbology')
          return store.settingsStore.toggleSymbology(symbologyOption.symbology).apply()
        }, [])}
        toggleAddon={useCallback((option: SymbologyOptionExtension) => {
          tracker.sendEvent('Setting', 'barcode-symbology-addon')
          return store.settingsStore.setExtensions([option.extension.name], !option.checked.peek()).apply()
        }, [])}
      />
    </>
  )
}

export default MainMenu
